import { defineAsyncComponent } from "vue";
import { beginLoading } from "@/shared/http/loading-bar";
import PageFailed from "./layout/PageFailed.vue";
import PageLoading from "./layout/PageLoading.vue";

/**
 * COMO UMA TELA CHEGA — e por que ela agora ABRE antes de chegar.
 *
 * ── O DEFEITO ─────────────────────────────────────────────────────────────
 *
 * As sessenta e uma telas do painel são carregadas sob demanda, e isso está
 * certo: a casca mais uma tela é o que alguém espera ao entrar, e uma delas
 * arrasta o mermaid inteiro. O que estava errado era o MOMENTO da troca.
 *
 * Com `component: () => import(...)`, o vue-router trata a rota como preguiçosa
 * e ESPERA o módulo antes de comitar a navegação. Enquanto o chunk não chega, o
 * `<RouterView>` continua a desenhar a tela ANTERIOR: o clique no menu não
 * produz nada, e "nada" é o que a pessoa lê como produto lento. Medido nos
 * bundles: 238 kB em Canais, 129 kB em Conversas, 123 kB em Entidade — segundos
 * numa rede fraca, imperceptível numa boa, e é essa variação que fez o dono
 * dizer "às vezes milissegundos, às vezes segundos".
 *
 * O esqueleto não podia salvar isso, e vale perceber porquê: os esqueletos do
 * painel vivem DENTRO do componente de cada tela, e o componente de cada tela é
 * exactamente o que está a ser descarregado. Não havia quem os desenhasse.
 *
 * ── A CORREÇÃO ────────────────────────────────────────────────────────────
 *
 * `defineAsyncComponent` devolve um componente JÁ RESOLVIDO — um invólucro que
 * o router aceita de imediato. A navegação comita, o endereço muda, a tela
 * "abre", e é o Vue que troca o `loadingComponent` pelo real quando o módulo
 * aterra. É a frase que se queria: a tela abre e mostra o esqueleto.
 *
 * O padrão não é novo aqui — `SettingsPage` monta as abas assim e o
 * `CommandPalette` monta o `QuickCreate` assim. O que faltava era o
 * `loadingComponent`, que é a razão de a aba de Ajustes também piscar em branco.
 *
 * ── AS TRÊS DECISÕES QUE ISTO CARREGA ─────────────────────────────────────
 *
 * 1. `delay` de 150 ms, e NÃO zero. Na segunda visita o módulo já está em
 *    memória e resolve num microtask; com zero, toda navegação já em cache
 *    piscaria um esqueleto por um frame — teríamos trocado uma espera por um
 *    tremeluzir, que é pior porque acontece sempre. Com o atraso, o que já é
 *    instantâneo continua instantâneo e o esqueleto só aparece quando há mesmo
 *    espera.
 *
 * 2. Uma repetição automática, e uma só. Um `import()` falha por soluço de rede
 *    com frequência suficiente para valer a pena tentar outra vez em silêncio, e
 *    raramente o suficiente para a segunda falha significar outra coisa — quase
 *    sempre um deploy que renomeou o ficheiro. Aí passa-se a bola ao
 *    `PageFailed`, que recarrega a página e vai buscar o `index.html` novo.
 *
 * 3. A falha é uma TELA e não uma navegação abortada. Antes, um chunk que não
 *    vinha rejeitava a navegação: o endereço não mudava e a interface congelava
 *    sem uma palavra. Isto é estritamente mais seguro do que o que havia.
 *
 * ── O QUE ISTO NÃO MUDA ───────────────────────────────────────────────────
 *
 * As guardas. `router.beforeEach` continua a correr ANTES de comitar — sessão,
 * papel, portal do parceiro, plataforma: tudo intacto, porque nada disso vive no
 * componente. A objecção clássica a este padrão é outra: o router não consegue
 * ler `beforeRouteEnter`/`Leave`/`Update` de dentro de um componente assíncrono,
 * porque nunca resolve o módulo para as ver. Neste painel não existe UMA única
 * guarda in-component (procurar as quatro por nome, se um dia surgir a dúvida),
 * então a objecção não se aplica — mas quem escrever a primeira tem de saber
 * que ela não será chamada por uma rota que passe por aqui.
 */
export function lazyPage(loader: () => Promise<unknown>) {
	return defineAsyncComponent({
		// O tipo do `loader` é frouxo de propósito: `import("./X.vue")` devolve um
		// módulo cujo `default` o TypeScript não estreita para `Component` sem uma
		// asserção em cada uma das sessenta e uma chamadas.
		//
		// A barra do topo acende enquanto o chunk desce. É a metade da navegação que
		// o router já não vê: ele comitou na hora (ver acima), e o esqueleto só
		// aparece depois de 150 ms — a barra cobre também esse intervalo. Cada
		// tentativa conta por si; só a que falha pinta de vermelho.
		loader: (() => {
			const end = beginLoading("route");
			return loader().then(
				(module) => {
					end();
					return module;
				},
				(error: unknown) => {
					end(true);
					throw error;
				},
			);
		}) as () => Promise<never>,
		loadingComponent: PageLoading,
		errorComponent: PageFailed,
		delay: 150,
		// Trinta segundos: um chunk de 240 kB numa rede muito má ainda chega, e o
		// que passa disto não é lentidão, é uma falha que merece ser dita.
		timeout: 30_000,
		onError(error, retry, fail, attempts) {
			if (attempts <= 1) retry();
			else fail();
			// `error` fica de fora de propósito: é a mensagem do bundler, não uma
			// frase para ninguém — quem fala com a pessoa é o `PageFailed`.
			void error;
		},
	});
}
